import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Radio, Trophy, Users, Layers, Gavel, Tv, RefreshCcw, ExternalLink, Monitor,
} from "lucide-react";
import { toast } from "sonner";

/**
 * /admin/display — Public Display Controller (§14-§26 of the Team Portal spec).
 *
 * Admin picks a mode → the /display route switches instantly. The 6 buttons
 * are large + kiosk-friendly. A small iframe preview mirrors what the venue
 * screen currently shows so admins can confirm before switching.
 */
const MODES = [
  { key: "auction",    label: "Auction",    icon: Gavel,   accent: "#F4B400", blurb: "Nominated player card only" },
  { key: "scoreboard", label: "Scoreboard", icon: Radio,   accent: "#E32424", blurb: "Live match scoreboard" },
  { key: "standings",  label: "Standings",  icon: Trophy,  accent: "#00A86B", blurb: "Group A / B / C tables" },
  { key: "bracket",    label: "Bracket",    icon: Layers,  accent: "#7DD3FC", blurb: "Knockout bracket" },
  { key: "lineup",     label: "Lineup",     icon: Users,   accent: "#38BDF8", blurb: "One team's seed pairings" },
  { key: "idle",       label: "Idle",       icon: Tv,      accent: "#94A3B8", blurb: "Mara League holding screen" },
];

export default function DisplayController() {
  const [state, setState] = useState({ mode: "idle", context: {}, updated_at: null, updated_by: null });
  const [busy, setBusy] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = async () => {
    try {
      const { data } = await api.get("/display/mode");
      setState(data);
    } catch (e) {
      toast.error("Failed to load display state");
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, []);

  const setMode = async (key) => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.put("/display/mode", { mode: key, context: state.context || {} });
      setState(data);
      setRefreshKey(k => k + 1);
      toast.success(`Public display → ${key.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to switch mode");
    } finally { setBusy(false); }
  };

  const displayUrl = useMemo(() => {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    return `${base.replace(/\/+$/, "")}/display`;
  }, []);

  const activeMode = MODES.find(m => m.key === state.mode) || MODES[5];

  return (
    <div className="p-6 md:p-10 max-w-7xl" data-testid="display-controller">
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#F4B400]">Broadcast Control</div>
        <h1 className="font-anton text-5xl md:text-6xl uppercase tracking-tighter text-white mt-1 leading-none">
          Public<br/><span className="text-[#F4B400]">Display.</span>
        </h1>
        <div className="mt-3 text-zinc-400 text-sm max-w-2xl">
          Pick a mode below and the venue screen ({displayUrl}) switches instantly.
          The audience only sees the selected mode — never your admin console.
        </div>
      </div>

      {/* Current mode banner */}
      <div
        className="mb-6 p-5 rounded-xl border-2 flex items-center gap-4"
        style={{ borderColor: activeMode.accent, background: `${activeMode.accent}15` }}
        data-testid="active-mode-banner"
      >
        <activeMode.icon className="w-8 h-8" style={{ color: activeMode.accent }} />
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-widest text-zinc-400">Currently showing</div>
          <div className="font-anton text-3xl uppercase tracking-tight text-white">{activeMode.label}</div>
          {state.updated_by && (
            <div className="text-xs text-zinc-500 mt-1">
              Set by {state.updated_by} · {state.updated_at ? new Date(state.updated_at).toLocaleTimeString() : ""}
            </div>
          )}
        </div>
        <a
          href={displayUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white text-black font-bold uppercase tracking-widest text-xs hover:bg-zinc-200"
          data-testid="open-display-btn"
        >
          <ExternalLink className="w-3.5 h-3.5" /> Open Display
        </a>
      </div>

      {/* Mode buttons */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
        {MODES.map(m => {
          const active = state.mode === m.key;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              disabled={busy}
              data-testid={`display-mode-btn-${m.key}`}
              className={`p-6 rounded-2xl border-2 text-left transition-all hover:scale-[1.02] disabled:opacity-50 ${
                active ? "shadow-2xl" : "border-white/10 bg-[#11241b] hover:border-white/30"
              }`}
              style={active ? { borderColor: m.accent, background: `${m.accent}20`, boxShadow: `0 0 0 4px ${m.accent}30` } : {}}
            >
              <m.icon className="w-8 h-8 mb-3" style={{ color: m.accent }} />
              <div className="font-anton text-3xl uppercase tracking-tight text-white leading-none">{m.label}</div>
              <div className="text-xs text-zinc-400 mt-2">{m.blurb}</div>
              {active && (
                <div className="mt-3 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: m.accent }}>
                  <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: m.accent }} />
                  Live on Screen
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Preview */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-[#F4B400]" />
            <div className="font-anton text-2xl uppercase tracking-tight text-white">Live Preview</div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setRefreshKey(k => k + 1)}
            data-testid="refresh-preview-btn"
            className="text-zinc-400 hover:text-white"
          >
            <RefreshCcw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
        </div>
        <div className="rounded-2xl border border-white/10 bg-black overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
          <iframe
            key={refreshKey}
            title="Public display preview"
            src={displayUrl}
            className="w-full h-full border-0"
            data-testid="display-preview-iframe"
          />
        </div>
        <div className="mt-2 text-xs text-zinc-500">
          Tip: press <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white/80">F11</kbd> on the venue browser for full-screen mode.
        </div>
      </div>
    </div>
  );
}
